import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import {
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  tap,
  takeUntil,
} from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';

@Component({
    selector: 'app-navbar-body',
    templateUrl: './navbar-body.component.html',
    styleUrls: ['./navbar-body.component.scss'],
    standalone: false
})
export class NavbarBodyComponent implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private permissionService = inject(PermissionService);

  @Input() isMinimized = false;
  @Output() menuItemSelected = new EventEmitter<void>();

  public readonly canViewProjects$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Project,
          UserCompanyPermissions.View,
        ),
      ),
      shareReplay(),
    );

  public readonly canViewEmployees$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Employee,
          UserCompanyPermissions.View,
        ),
      ),
      shareReplay(),
    );

  public readonly canViewAssignments$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Assignment,
          UserCompanyPermissions.View,
        ),
      ),
      shareReplay(),
    );

  public readonly canViewEmployeesAssignments$ = combineLatest([
    this.canViewProjects$,
    this.canViewAssignments$,
  ]).pipe(
    map(
      ([canViewProject, canViewAssignments]) =>
        canViewAssignments && canViewProject,
    ),
    shareReplay()
  );

  public readonly canViewKanban$ = this.canViewEmployeesAssignments$.pipe(
    switchMap((canViewEmployeesAssignments) =>
      this.permissionService.userPermissions$.pipe(
        map(
          (permissions) =>
            this.permissionService.hasPermission(
              permissions,
              ResourceTypes.Assignment,
              UserCompanyPermissions.Edit,
            ) && canViewEmployeesAssignments,
        ),
      ),
    ),
  );

  public readonly canEditCompany$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Company,
          UserCompanyPermissions.Edit,
        ),
      ),
    );

  public readonly canViewContacts$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Contact,
          UserCompanyPermissions.View,
        ),
      ),
    );

  public readonly canViewOrganizations$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Organization,
          UserCompanyPermissions.View,
        ),
      ),
    );

  public readonly canViewGoods$ = this.canViewOrganizations$;
  public readonly canViewSalesOrders$ = this.canViewOrganizations$;
  // Top-level invoices page surfaces under the same sales-side permission gate as sales
  // orders. TODO: tighten to a dedicated invoice permission once one exists.
  public readonly canViewInvoices$ = this.canViewOrganizations$;
  public readonly canViewStatistics$ = this.canViewEmployeesAssignments$;
  public readonly companiesLandingCommands = ['/', ...this.authService.getCompaniesLandingUrl().split('/')];

  private unsubscribe$ = new Subject<void>();

  constructor() {
    this.router.events
      .pipe(
        filter((x) => x instanceof NavigationStart),
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.menuItemSelected.emit();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
